import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../../../constants';
import SearchSupport from '../../../supports/SearchSupport';
import FadeImage from '../../../shared/FadeImage';
import { useTranslation } from 'react-i18next';

const SupportList = ({ onSelect, data }) => {
    const { t } = useTranslation();
    const [supports, setSupports] = useState([]);
    const [filteredSupports, setFilteredSupports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedType, setSelectedType] = useState('All');
    const [selectedRarity, setSelectedRarity] = useState('All');
    const [displayLimit, setDisplayLimit] = useState(36); // Initial batch size

    useEffect(() => {
        if (data && data.matrix) {
            // Transform matrix object to array for the list
            // Matrix format: { "1001": { info: {...}, effects: {...} }, ... }
            // We need flat array: [ { support_id: "1001", ...info }, ... ]
            try {
                const list = Object.entries(data.matrix).map(([id, card]) => ({
                    ...card.info,
                    support_id: id, // Ensure ID is accessible
                    // Keeping effects might be heavy but useful for filtering later if needed
                    // For now, list only needs info
                }));
                // Sort by ID or Rarity? Usually ID desc or Name
                list.sort((a, b) => b.support_id - a.support_id);

                setSupports(list);
                setFilteredSupports(list);
                setLoading(false);
            } catch (e) {
                console.error("Error parsing matrix data for list:", e);
                setError("Failed to parse data");
                setLoading(false);
            }
        } else if (!data) {
            setLoading(true);
        }
    }, [data]);

    useEffect(() => {
        let results = supports;

        if (selectedType !== 'All') {
            results = results.filter(s => s.Support_Type === selectedType);
        }

        if (selectedRarity !== 'All') {
            results = results.filter(s => s.rarity === selectedRarity);
        }

        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            results = results.filter(s =>
                (s.char_name && s.char_name.toLowerCase().includes(term)) ||
                (s.card_name && s.card_name.toLowerCase().includes(term))
            );
        }

        setFilteredSupports(results);
        setDisplayLimit(36);
    }, [searchTerm, selectedType, selectedRarity, supports]);

    const handleScroll = (e) => {
        const { scrollTop, clientHeight, scrollHeight } = e.target;
        if (scrollHeight - scrollTop <= clientHeight + 100) {
            if (displayLimit < filteredSupports.length) {
                setDisplayLimit(prev => prev + 36);
            }
        }
    };

    const getTypeIconPath = (supportType) => {
        if (!supportType) return '';
        const type = supportType.charAt(0).toUpperCase() + supportType.slice(1).toLowerCase();
        if (['Speed', 'Stamina', 'Power', 'Guts', 'Wit'].includes(type)) {
            return `/images/Icon/Stats_icons/Stat_Type_${type}.png`;
        } else if (['Pal', 'Group'].includes(type)) {
            return `/images/Icon/Support_Icons/Support_Type_${type}.png`;
        }
        return '';
    };

    if (loading) return <div className="text-center p-8">{t('SupportList.loading')}</div>;
    if (error) return <div className="text-center p-8 text-red-500">{t('SupportList.error_message', { error })}</div>;

    const visibleSupports = filteredSupports.slice(0, displayLimit);

    return (
        <div className="w-full">
            <SearchSupport
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                selectedType={selectedType}
                onTypeChange={setSelectedType}
                selectedRarity={selectedRarity}
                onRarityChange={setSelectedRarity}
            />

            <div
                className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3 overflow-y-auto max-h-[60vh] p-1"
                onScroll={handleScroll}
            >
                {visibleSupports.map(support => {
                    const getThumbnailUrl = (originalPath, width) => {
                        return `${API_BASE_URL}/api/thumb?img=${originalPath}&width=${width}`;
                    };

                    const cardIconPath = getThumbnailUrl(
                        `/images/Icon/Support_Character_Icon/Support_Card_${support.support_id}_Icon.png`,
                        160
                    );
                    const typeIconPath = getTypeIconPath(support.Support_Type);

                    return (
                        <div
                            key={support.Id || support.support_id}
                            onClick={() => onSelect(support)}
                            className="flex flex-col items-center cursor-pointer group relative p-1 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            <div className="relative w-20 h-20 md:w-24 md:h-24 shadow-sm rounded-lg overflow-hidden border border-gray-200 group-hover:border-blue-400 group-hover:shadow-md transition-all">
                                <FadeImage
                                    src={cardIconPath}
                                    className="w-full h-full"
                                    alt={`${support.char_name}`}
                                    loading="lazy"
                                />
                                {typeIconPath && (
                                    <img
                                        src={typeIconPath}
                                        className="absolute bottom-0 right-0 w-6 h-6 object-contain z-10 bg-white/80 rounded-tl-md"
                                        alt={support.Support_Type}
                                    />
                                )}
                            </div>
                            <p className="mt-1 text-[10px] md:text-xs text-center font-medium line-clamp-2 text-gray-700 w-full group-hover:text-blue-600">
                                {support.char_name || support.name}
                            </p>
                        </div>
                    );
                })}
                {displayLimit < filteredSupports.length && (
                    <div className="col-span-full py-2 text-center text-gray-400 text-xs animate-pulse">
                        {t('SupportList.loading_more')}
                    </div>
                )}
            </div>
            {filteredSupports.length === 0 && (
                <p className="text-center text-gray-500 py-8">{t('SupportList.empty_state')}</p>
            )}
        </div>
    );
};

export default SupportList;
